/**
 * F-099 / F-050 / F-120 — parts of a product line: PIQ operations (modifiers), sourcing presets
 * and supplier-info layouts. SOURCE OF TRUTH for the SPA; pinned byte-for-byte against the
 * shared contract `contracts/dd-part-operations.json` (partOperationsContract.test.ts), which
 * the API pins too (DdPartOperations.php). Change the JSON and both stacks together.
 *
 * Data: Operation Codes tab v3 (Marguerite Mazza / Amanda Skalabrin, 2026-10-02) — 10
 * operation types, 38 PIQ codes, the input each one needs; supplier layouts and sourcing
 * presets as confirmed by Candace Loran and Amanda Skalabrin (2026-10-05). Scoring has no
 * PIQ operation yet (Amanda) and stays in the part description.
 */

/** What D&D enters for an operation — mirrors `input_kinds` in the contract. */
export type PartOperationInput = 'qty' | 'spacing_in' | 'size_qty' | 'bends' | 'none'

export type PartSourcingKey =
  | 'manufactured_in_house'
  | 'hardware_in_house'
  | 'hardware_drop_shipped'
  | 'vended_drop_shipped'

export type SupplierLayoutKey = 'stocked_hardware' | 'drop_shipped'

export interface PartSourcing {
  key: PartSourcingKey
  label: string
  /** Only an in-house manufactured part has a stock to pick. */
  needs_substrate: boolean
  /** The supplier-info layout this sourcing opens (null = no supplier boxes). */
  supplier_layout: SupplierLayoutKey | null
}

export interface SupplierLayoutField {
  key: string
  label: string
  /** Required boxes must be non-empty ("N/A" counts) before Triage can be completed. */
  required: boolean
}

export interface SupplierLayout {
  key: SupplierLayoutKey
  label: string
  fields: SupplierLayoutField[]
}

export interface PartOperationSize {
  value: string
  label: string
}

export interface PartOperation {
  /** The PIQ operation code — what the HubSpot note and PIQ print. */
  code: string
  /** The intake name the department picks. */
  name: string
  input: PartOperationInput
  /** Holes only: the companion PIQ code for the size row. */
  size_code?: string
  /** Holes only: the fixed sizes ("custom" = see instructions). */
  sizes?: PartOperationSize[]
  /** Sleeves: stocked vs special order. */
  stock?: 'stocked' | 'special_order'
  /** Shown as a popup when this option is picked. */
  warning?: string
}

export interface PartOperationType {
  key: string
  label: string
  /** Shown as a popup when the type is picked. */
  notice?: string
  operations: PartOperation[]
}

export interface PartOperationsContract {
  version: number
  source: string
  input_kinds: Record<PartOperationInput, string>
  sourcing: PartSourcing[]
  supplier_layouts: SupplierLayout[]
  operation_types: PartOperationType[]
}

export const PART_OPERATIONS_CONTRACT_VERSION = 1

export const PART_OPERATIONS_CONTRACT_SOURCE =
  'Hardware Codes and Operations — Operation Codes tab (Marguerite Mazza / Amanda Skalabrin, v3 2026-10-02) + F-050 / F-112 supplier layouts (Candace Loran, Amanda Skalabrin 2026-10-05).'

export const PART_OPERATION_INPUT_KINDS: Record<PartOperationInput, string> = {
  qty: 'whole number ≥ 1',
  spacing_in: 'space between, inches, > 0',
  size_qty: 'one row per hole size: size from `sizes` (or custom) + whole number ≥ 1',
  bends: 'number of bends, whole number ≥ 1',
  none: 'no additional input'
}

export const PART_SOURCING: readonly PartSourcing[] = [
  {
    key: 'manufactured_in_house',
    label: 'Manufactured – In House',
    needs_substrate: true,
    supplier_layout: null
  },
  {
    key: 'hardware_in_house',
    label: 'Hardware – In House',
    needs_substrate: false,
    supplier_layout: 'stocked_hardware'
  },
  {
    key: 'hardware_drop_shipped',
    label: 'Hardware – Drop Shipped',
    needs_substrate: false,
    supplier_layout: 'drop_shipped'
  },
  {
    key: 'vended_drop_shipped',
    label: 'Vended – Drop Shipped',
    needs_substrate: false,
    supplier_layout: 'drop_shipped'
  }
]

export const SUPPLIER_LAYOUTS: readonly SupplierLayout[] = [
  {
    key: 'stocked_hardware',
    label: 'Stocked Hardware (In-House Hardware)',
    fields: [
      {
        key: 'internal_shorthand',
        label: 'Internal Shorthand',
        required: true
      },
      {
        key: 'uom_purchased',
        label: 'Unit of Measure for Units Purchased',
        required: true
      },
      {
        key: 'uom_sold',
        label: 'Unit of Measure for How it is Sold',
        required: true
      },
      {
        key: 'purchase_price',
        label: 'Purchase Price',
        required: true
      },
      {
        key: 'sale_price',
        label: 'Sale Price',
        required: true
      },
      {
        key: 'supplier_url',
        label: 'Supplier URL',
        required: true
      },
      {
        key: 'supplier_part_number',
        label: 'Supplier Part Number',
        required: true
      },
      {
        key: 'additional_info',
        label: 'Additional Info',
        required: false
      }
    ]
  },
  {
    key: 'drop_shipped',
    label: 'Drop Shipped Items (Outsourced Hardware & Vended Products)',
    fields: [
      {
        key: 'supplier',
        label: 'Supplier',
        required: true
      },
      {
        key: 'supplier_url',
        label: 'Supplier URL',
        required: true
      },
      {
        key: 'supplier_part_number',
        label: 'Supplier Part Number',
        required: true
      },
      {
        key: 'additional_info',
        label: 'Additional Info',
        required: false
      }
    ]
  }
]

export const PART_OPERATION_TYPES: readonly PartOperationType[] = [
  {
    key: 'grommets',
    label: 'Grommets',
    notice:
      'If the grommet configuration you require is not an available option, choose Grommet Special (Custom – See instructions) and provide the size required in the text box.',
    operations: [
      {
        code: 'GROM 2 CNR',
        name: 'Grommet 2 Corners',
        input: 'qty'
      },
      {
        code: 'GROM 4 CNR',
        name: 'Grommet 4 Corners',
        input: 'qty'
      },
      {
        code: 'GROM 4CNR & SPC',
        name: 'Grommet 4 Corners & Spaced',
        input: 'spacing_in'
      },
      {
        code: 'GROM SGL CTR',
        name: 'Grommet Single Center',
        input: 'qty'
      },
      {
        code: 'GROM SPL',
        name: 'Grommet Special (Custom – see instructions)',
        input: 'qty'
      },
      {
        code: 'GROM TOP & SPC',
        name: 'Grommets Top and Spaced',
        input: 'spacing_in'
      }
    ]
  },
  {
    key: 'vhb',
    label: 'VHB',
    operations: [
      {
        code: 'VHB STRIPS',
        name: 'VHB Strips – Qty',
        input: 'qty'
      },
      {
        code: 'VHB STRIPS INCLUDED',
        name: 'VHB Strips – 4 Corners + Spaced',
        input: 'spacing_in'
      }
    ]
  },
  {
    key: 'mounting_adhesive',
    label: 'Mounting Adhesive',
    operations: [
      {
        code: 'MOUNTING ADHESIVE',
        name: 'Pre-Cut Mounting Adhesive (Smooth Surface)',
        input: 'none'
      }
    ]
  },
  {
    key: 'holes',
    label: 'Holes',
    notice:
      'If the hole size you require is not an available option, choose Custom – See instructions and provide the size required in the text box. For products with multiple hole sizes, make sure to provide the qty for each of the hole sizes needed.',
    operations: [
      {
        code: 'HOLES',
        name: 'Holes',
        input: 'size_qty',
        size_code: 'HOLE SIZE',
        sizes: [
          {
            value: '.125',
            label: '.125 (1/8")'
          },
          {
            value: '.1875',
            label: '.1875 (3/16")'
          },
          {
            value: '.25',
            label: '.25 (1/4")'
          },
          {
            value: '.375',
            label: '.375 (u channel)'
          },
          {
            value: '.5',
            label: '.5 (1/2")'
          },
          {
            value: '.625',
            label: '.625 (5/8")'
          },
          {
            value: '.75',
            label: '.75 (3/4")'
          },
          {
            value: 'custom',
            label: 'Custom – see instructions'
          }
        ]
      }
    ]
  },
  {
    key: 'heat_bending',
    label: 'Heat Bending',
    operations: [
      {
        code: 'HEAT BENDING',
        name: 'Heat Bending',
        input: 'bends'
      }
    ]
  },
  {
    key: 'app_mask',
    label: 'App Mask',
    operations: [
      {
        code: 'APP MASK',
        name: 'Application Mask – For rigid products with vinyl applied',
        input: 'none'
      }
    ]
  },
  {
    key: 'app_tape_mask',
    label: 'App Tape Mask',
    operations: [
      {
        code: 'APP TAPE/MASK',
        name: 'Application Transfer Tape – For loose vinyl',
        input: 'none'
      }
    ]
  },
  {
    key: 'zip_ties',
    label: 'Zip Ties',
    operations: [
      {
        code: 'ZIP TIES',
        name: 'Zip Ties',
        input: 'qty'
      }
    ]
  },
  {
    key: 'sleeves_long',
    label: 'Sleeves – Long',
    notice:
      'Sleeve names read: insert size / footprint size / L or S (opening on the long or short side). Codes are for the insert size, not the full sleeve.',
    operations: [
      {
        code: 'SLEEVE-LONG-11X8.5IN',
        name: 'Sleeve (Stocked) 11x8.5 / 11.5x9.5 / L',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-LONG-18X12IN',
        name: 'Sleeve (Stocked) 18x12 / 18.5x12.625 / L',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-LONG-6X4IN',
        name: 'Sleeve (Stocked) 6x4 / 6.25x4.25 / L',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-LONG-9X6IN',
        name: 'Sleeve (Special Order) 9x6 / 9.875x6.5 / L',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-LONG-8X5IN',
        name: 'Sleeve (Special Order) 8x5 / 8.375x5.5 / L',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-LONG-5X3IN',
        name: 'Sleeve (Special Order) 5x3 / 5.375x3.5 / L',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-LONG-4X3.5IN',
        name: 'Sleeve (Special Order) 4x3.5 / 4.375x3.875 / L',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-LONG-3.5X2IN',
        name: 'Sleeve (Special Order) 3.5x2 / 3.875x2.5 / L',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      }
    ]
  },
  {
    key: 'sleeves_short',
    label: 'Sleeves – Short',
    notice:
      'Sleeve names read: insert size / footprint size / L or S (opening on the long or short side). Codes are for the insert size, not the full sleeve.',
    operations: [
      {
        code: 'SLEEVE-SHORT-11X17IN',
        name: 'Sleeve (Stocked) 11x17 / 11.375x17.375 / S',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-SHORT-4X6IN',
        name: 'Sleeve (Stocked) 4x6 / 4.375x6.375 / S',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-SHORT-3X5IN',
        name: 'Sleeve (Stocked) 3x5 / 3.25x5.25 / S',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-SHORT-6X9IN',
        name: 'Sleeve (Stocked) 6x9 / 6.375x9.375 / S',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-SHORT-9X12IN',
        name: 'Sleeve (Stocked) 9x12 / 9.375x12.375 / S',
        input: 'qty',
        stock: 'stocked'
      },
      {
        code: 'SLEEVE-SHORT-12X18IN',
        name: 'Sleeve (Special Order) 12x18 / 12.375x18.5 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-10X12IN',
        name: 'Sleeve (Special Order) 10x12 / 10.25x12.25 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-5X7IN',
        name: 'Sleeve (Special Order) 5x7 / 5.375x7.375 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-5X5IN',
        name: 'Sleeve (Special Order) 5x5 / 5.375x5.5 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-4X8IN',
        name: 'Sleeve (Special Order) 4x8 / 4.375x8.5 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-4X4IN',
        name: 'Sleeve (Special Order) 4x4 / 4.375x4.375 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-2X3.5IN',
        name: 'Sleeve (Special Order) 2x3.5 / 3.25x5.25 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-1X4IN',
        name: 'Sleeve (Special Order) 1x4 / 1.375x4.375 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-5X8IN',
        name: 'Sleeve (Special Order) 5x8 / 5.5x5.375 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-8.5X11IN',
        name: 'Sleeve (Special Order) 8.5x11 / 8.875x11.375 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      },
      {
        code: 'SLEEVE-SHORT-8.5X14IN',
        name: 'Sleeve (Special Order) 8.5x14 / 9x14.65 / S',
        input: 'qty',
        stock: 'special_order',
        warning:
          'Default to Stocked sleeves; use Special Order only when required by customer specifications.'
      }
    ]
  }
]

/** The whole catalog in the contract's shape — what the contract test compares. */
export function partOperationsContract(): PartOperationsContract {
  return {
    version: PART_OPERATIONS_CONTRACT_VERSION,
    source: PART_OPERATIONS_CONTRACT_SOURCE,
    input_kinds: { ...PART_OPERATION_INPUT_KINDS },
    sourcing: PART_SOURCING.map((s) => ({ ...s })),
    supplier_layouts: SUPPLIER_LAYOUTS.map((l) => ({
      ...l,
      fields: l.fields.map((f) => ({ ...f }))
    })),
    operation_types: PART_OPERATION_TYPES.map((t) => ({
      ...t,
      operations: t.operations.map((o) => ({ ...o }))
    }))
  }
}

const OPERATION_BY_CODE: ReadonlyMap<
  string,
  { type: PartOperationType; operation: PartOperation }
> = new Map(
  PART_OPERATION_TYPES.flatMap((type) =>
    type.operations.map((operation) => [operation.code, { type, operation }] as const)
  )
)

/** The operation behind a PIQ code (null for an unknown code). */
export function findPartOperation(
  code: string
): { type: PartOperationType; operation: PartOperation } | null {
  return OPERATION_BY_CODE.get(code) ?? null
}

/** Every PIQ code the catalog knows, in catalog order. */
export function partOperationCodes(): string[] {
  return PART_OPERATION_TYPES.flatMap((t) => t.operations.map((o) => o.code))
}

export function findPartSourcing(key: string): PartSourcing | null {
  return PART_SOURCING.find((s) => s.key === key) ?? null
}

export function findSupplierLayout(key: string): SupplierLayout | null {
  return SUPPLIER_LAYOUTS.find((l) => l.key === key) ?? null
}
